import { memo, useMemo, useRef, useState } from "react";
import useCurrentUser from "@/hooks/useCurrentUser";
import { useMaxWidth } from "@/hooks/useMediaQuery";
import { useUser } from "@/hooks/useUserQueries";
import { cn } from "@/lib/utils";
import { State } from "@/types/proto/api/v1/common_pb";
import { isSuperUser } from "@/utils/user";
import JournalEditor from "../JournalEditor";
import JournalPriceSnapshotBadges from "../JournalPriceSnapshotBadges";
import PreviewImageDialog from "../PreviewImageDialog";
import { JournalBody, JournalHeader } from "./components";
import { MEMO_CARD_BASE_CLASSES } from "./constants";
import { useImagePreview, useJournalActions, useJournalHandlers, useNsfwContent } from "./hooks";
import { JournalViewContext } from "./JournalViewContext";
import type { JournalViewProps } from "./types";

const JournalView: React.FC<JournalViewProps> = (props: JournalViewProps) => {
  const { memo: memoData, className, parentPage: parentPageProp } = props;
  const cardRef = useRef<HTMLDivElement>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [editSessionId, setEditSessionId] = useState(0);

  const currentUser = useCurrentUser();
  const creator = useUser(memoData.creator).data;
  const isArchived = memoData.state === State.ARCHIVED;
  const readonly = memoData.creator !== currentUser?.name && !isSuperUser(currentUser);
  const parentPage = parentPageProp || "/";
  const isMobile = useMaxWidth("sm"); // Mobile: < 640px
  const hasPriceSnapshots = memoData.priceSnapshots && memoData.priceSnapshots.length > 0;

  const { nsfw, showNSFWContent, toggleNsfwVisibility } = useNsfwContent(memoData, props.showNsfwContent);
  const { previewState, openPreview, setPreviewOpen } = useImagePreview();
  const { unpinMemo } = useJournalActions(memoData, isArchived);

  const handleEditorConfirm = () => setShowEditor(false);
  const handleEditorCancel = () => setShowEditor(false);
  const openEditor = () => {
    setEditSessionId((id) => id + 1);
    setShowEditor(true);
  };

  const { handleGotoJournalDetailPage, handleJournalContentClick, handleJournalContentDoubleClick } = useJournalHandlers({
    memoName: memoData.name,
    parentPage,
    readonly,
    openEditor,
    openPreview,
  });

  const contextValue = useMemo(
    () => ({
      memo: memoData,
      creator,
      currentUser,
      parentPage,
      isArchived,
      readonly,
      showNSFWContent,
      nsfw,
    }),
    [memoData, creator, currentUser, parentPage, isArchived, readonly, showNSFWContent, nsfw],
  );

  if (showEditor) {
    return (
      <JournalEditor
        key={`${memoData.name}-${editSessionId}`}
        autoFocus
        className="mb-2"
        cacheKey={`inline-memo-editor-${memoData.name}-${editSessionId}`}
        memoName={memoData.name}
        onConfirm={handleEditorConfirm}
        onCancel={handleEditorCancel}
      />
    );
  }

  return (
    <JournalViewContext.Provider value={contextValue}>
      <article className={cn(MEMO_CARD_BASE_CLASSES, className)} ref={cardRef} tabIndex={readonly ? -1 : 0}>
        <JournalHeader
          showCreator={props.showCreator}
          // ❌ REMOVED: showVisibility prop (v0.66.0 - all memos are private)
          showPinned={props.showPinned}
          onEdit={openEditor}
          onGotoDetail={handleGotoJournalDetailPage}
          onUnpin={unpinMemo}
          onToggleNsfwVisibility={toggleNsfwVisibility}
        />

        {/* Main content area with optional badges */}
        <div className="flex flex-row justify-start items-start w-full gap-0">
          {/* Left: Memo content */}
          <div className="flex-1 min-w-0">
            <JournalBody
              compact={props.compact}
              onContentClick={handleJournalContentClick}
              onContentDoubleClick={handleJournalContentDoubleClick}
              onToggleNsfwVisibility={toggleNsfwVisibility}
              onAiPreviewClick={handleGotoJournalDetailPage}
            />

            {/* Mobile: Badges at bottom of content */}
            {isMobile && hasPriceSnapshots && (
              <div className="mt-2 w-full">
                <JournalPriceSnapshotBadges priceSnapshots={memoData.priceSnapshots} variant="mobile" />
              </div>
            )}
          </div>

          {/* Desktop: Badges on right side */}
          {!isMobile && hasPriceSnapshots && (
            <div className="ml-3 shrink-0">
              <JournalPriceSnapshotBadges priceSnapshots={memoData.priceSnapshots} variant="desktop" />
            </div>
          )}
        </div>

        <PreviewImageDialog
          open={previewState.open}
          onOpenChange={setPreviewOpen}
          imgUrls={previewState.urls}
          initialIndex={previewState.index}
        />
      </article>
    </JournalViewContext.Provider>
  );
};

export default memo(JournalView);
